import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Mail, MapPin, Phone, Clock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Contact() {
  const [info, setInfo] = useState({});
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/site-info`).then((r) => setInfo(r.data)).catch(() => {});
  }, []);

  function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Udfyld navn, email og besked");
      return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/public/contact`, form);
      setSent(true);
    } catch {
      toast.error("Noget gik galt — prøv igen");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="bg-[#F7F3EC] min-h-screen" data-testid="contact-page">
      <section className="py-16 md:py-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20">
          <div>
            <div className="label-tag mb-4">Kontakt</div>
            <h1 className="font-heading text-5xl md:text-6xl text-graphite mb-6 leading-tight">{(info.contact_title && info.contact_title.trim()) || "Skriv til os."}</h1>
            <p className="text-graphite/75 text-base md:text-lg mb-10 max-w-md leading-relaxed whitespace-pre-line">
              {(info.contact_subtitle && info.contact_subtitle.trim()) || "Spørgsmål om en bil, en aftale, eller bare for at sige hej — vi svarer altid hurtigt."}
            </p>
            <div className="space-y-5">
              {info.contact_email && <InfoRow icon={<Mail size={18} strokeWidth={1.5}/>} label="Email" value={info.contact_email} href={`mailto:${info.contact_email}`}/>}
              {info.contact_phone && <InfoRow icon={<Phone size={18} strokeWidth={1.5}/>} label="Telefon" value={info.contact_phone} href={`tel:${info.contact_phone}`}/>}
              {info.contact_address && <InfoRow icon={<MapPin size={18} strokeWidth={1.5}/>} label="Adresse" value={info.contact_address}/>}
              {info.contact_hours && <InfoRow icon={<Clock size={18} strokeWidth={1.5}/>} label="Åbningstider" value={info.contact_hours}/>}
            </div>
          </div>
          <div>
            {sent ? (
              <div className="bg-white rounded-2xl p-10 border border-[#E5DED1] text-center" data-testid="contact-success">
                <div className="font-heading text-3xl text-graphite mb-3">Tak for din besked!</div>
                <p className="text-graphite/70">Vi vender tilbage snarest muligt.</p>
              </div>
            ) : (
              <form onSubmit={submit} className="bg-white rounded-2xl p-6 md:p-8 border border-[#E5DED1] space-y-4" data-testid="contact-form">
                <input className="public-input" placeholder="Navn" value={form.name} onChange={(e) => set("name", e.target.value)} data-testid="contact-name"/>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <input className="public-input" placeholder="Email" type="email" value={form.email} onChange={(e) => set("email", e.target.value)} data-testid="contact-email"/>
                  <input className="public-input" placeholder="Telefon (valgfrit)" value={form.phone} onChange={(e) => set("phone", e.target.value)} data-testid="contact-phone"/>
                </div>
                <textarea className="public-input resize-none" placeholder="Besked" rows="6" value={form.message} onChange={(e) => set("message", e.target.value)} data-testid="contact-message"/>
                <button type="submit" disabled={busy} className="btn-primary justify-center w-full" data-testid="contact-submit">
                  {busy ? "Sender…" : "Send besked"}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>
    </div>
  );
}

function InfoRow({ icon, label, value, href }) {
  const inner = (
    <div className="flex items-start gap-3">
      <span className="text-copper mt-0.5">{icon}</span>
      <div>
        <div className="text-[10px] uppercase tracking-wider text-graphite/50 mb-0.5">{label}</div>
        <div className="text-graphite font-medium">{value}</div>
      </div>
    </div>
  );
  return href ? <a href={href} className="hover:text-copper transition-colors block">{inner}</a> : inner;
}
